"use strict";
/* --------------------------------------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License. See License.txt in the project root for license information.
 * ------------------------------------------------------------------------------------------ */
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.TextDocumentContentFeature = void 0;
const vscode = __importStar(require("vscode"));
const vscode_languageserver_protocol_1 = require("vscode-languageserver-protocol");
const features_1 = require("./features");
const UUID = __importStar(require("./utils/uuid"));
class TextDocumentContentFeature {
    _client;
    _registrations = new Map();
    constructor(client) {
        this._client = client;
    }
    getState() {
        const registrations = this._registrations.size > 0;
        return { kind: 'workspace', id: vscode_languageserver_protocol_1.TextDocumentContentRequest.method, registrations };
    }
    get registrationType() {
        return vscode_languageserver_protocol_1.TextDocumentContentRequest.type;
    }
    getProviders() {
        const result = [];
        for (const registration of this._registrations.values()) {
            result.push(...registration.providers);
        }
        return result;
    }
    fillClientCapabilities(capabilities) {
        const textDocumentContent = (0, features_1.ensure)((0, features_1.ensure)(capabilities, 'workspace'), 'textDocumentContent');
        textDocumentContent.dynamicRegistration = true;
    }
    initialize(capabilities) {
        const client = this._client;
        client.onRequest(vscode_languageserver_protocol_1.TextDocumentContentRefreshRequest.type, async (params) => {
            const uri = client.protocol2CodeConverter.asUri(params.uri);
            for (const registrations of this._registrations.values()) {
                for (const provider of registrations.providers) {
                    if (provider.scheme === uri.scheme) {
                        provider.onDidChangeEmitter.fire(uri);
                    }
                }
            }
        });
        if (!capabilities?.workspace?.textDocumentContent) {
            return;
        }
        const capability = capabilities.workspace.textDocumentContent;
        const id = vscode_languageserver_protocol_1.StaticRegistrationOptions.hasId(capability) ? capability.id : UUID.generateUuid();
        this.register({
            id: id,
            registerOptions: capability
        });
    }
    register(data) {
        const registrations = [];
        const disposables = [];
        for (const scheme of data.registerOptions.schemes) {
            const [disposable, registration] = this.registerTextDocumentContentProvider(scheme);
            registrations.push(registration);
            disposables.push(disposable);
        }
        this._registrations.set(data.id, { disposable: vscode.Disposable.from(...disposables), providers: registrations });
    }
    registerTextDocumentContentProvider(scheme) {
        const eventEmitter = new vscode.EventEmitter();
        const provider = {
            onDidChange: eventEmitter.event,
            provideTextDocumentContent: (uri, token) => {
                const client = this._client;
                const provideTextDocumentContent = (uri, token) => {
                    const params = {
                        uri: client.code2ProtocolConverter.asUri(uri)
                    };
                    return client.sendRequest(vscode_languageserver_protocol_1.TextDocumentContentRequest.type, params, token).then((result) => {
                        if (token.isCancellationRequested) {
                            return null;
                        }
                        return result.text;
                    }, (error) => {
                        return client.handleFailedRequest(vscode_languageserver_protocol_1.TextDocumentContentRequest.type, token, error, null);
                    });
                };
                const middleware = client.middleware;
                return middleware.provideTextDocumentContent
                    ? middleware.provideTextDocumentContent(uri, token, provideTextDocumentContent)
                    : provideTextDocumentContent(uri, token);
            }
        };
        return [vscode.workspace.registerTextDocumentContentProvider(scheme, provider), { scheme, onDidChangeEmitter: eventEmitter, provider }];
    }
    unregister(id) {
        const registration = this._registrations.get(id);
        if (registration !== undefined) {
            this._registrations.delete(id);
            registration.disposable.dispose();
        }
    }
    clear() {
        this._registrations.forEach((registration) => {
            registration.disposable.dispose();
        });
        this._registrations.clear();
    }
}
exports.TextDocumentContentFeature = TextDocumentContentFeature;
